import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'
import * as componentStore from '../component-store'

import * as bar from './bar'
import * as donut from './donut'
import * as line from './line'
import * as scatter from './scatter'
import * as sunburst from './sunburst'
import * as venn from './venn'

// TODO: make this file more DRY

// TODO: why are there color palettes in here and in the footer? Shouldn't there
// be one for both?

@ngCore.Component({
  imports: [
    ds.chart.Component,
    ds.layout.widget.Component,
    ds.illustration.emptyState.Component,
    scatter.Component,
    donut.Component,
    bar.Component,
    line.Component,
    sunburst.Component,
    venn.Component,
  ],
  selector: 'atn-mosaic-chart',
  standalone: true,
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
/**
 * Displays collection of charts by calling appropriate component depending on the chart type
 */
export class Component {
  /** @see {@link componentStore.Widget} */
  public readonly widgetStore = ngCore.inject(
    componentStore.Widget<ents.core.mosaic.Widget.Type.Charts>,
  )

  /** Exposing chart type enum @see {@link ents.core.display.chart.Type}  */
  public ChartTypeEnum = ents.core.display.chart.Type

  /** TODO:  */
  private readonly _dataSg = ngCore.computed(
    (content = this.widgetStore.contentSg()) => content?.data ?? [],
  )

  /** TODO: */
  private readonly _typeSg = ngCore.computed(
    (content = this.widgetStore.contentSg()) => content?.type,
  )

  /** @see {@link state.mosaic.endUser.currentRendering.Feature} */
  private readonly _currentRendering = ngCore.inject(
    state.mosaic.endUser.currentRendering.Feature,
  )

  /** Available colors for the current charts */
  public readonly paletteSg = ngCore.computed((type = this._typeSg()) =>
    ds.chart.colors.createPalette(type),
  )

  /** TODO */
  public readonly chartsIfDonutSg = ngCore.computed(
    (
      data = this._dataSg(),
      type = this._typeSg(),
      context = this._currentRendering.selectors.context.asSignal(),
      widgetId = this.widgetStore.renderingIdSg(),
      refId = this.widgetStore.refIdSg(),
    ) => {
      if (type === ents.core.display.chart.Type.Donut) {
        // TODO: there should be a way to utilize `content` as a discriminate
        // union so that this casting is unnecessary
        const sliceCollection =
          data as readonly ents.core.display.chart.data.Of<typeof type>[]
        const palette = this.paletteSg()

        return sliceCollection.map(
          collection =>
            ({
              config: {
                onItemClick: ({ onClick: actionRef, name }) => {
                  if (actionRef && context && widgetId) {
                    this._currentRendering.subfeatures.actions
                      .createActionHandler(`Chart.Slice-${name}.Clicked`, {
                        ...context,
                        actionRef,
                        widget: {
                          id: widgetId,
                          refId,
                        },
                      })
                      .execute()
                  }
                },
              },
              data: {
                ...collection,
                slices: collection.slices?.map((slice, i) => ({
                  ...slice,
                  color: palette.getOrAssignColorFor(
                    slice.name ?? i.toString(),
                  ),
                })),
              },
              type,
            }) satisfies ents.core.display.chart.Chart<ents.core.display.chart.Type.Donut>,
        )
      }

      return undefined
    },
  )

  /** TODO */
  public chartsIfLineSg = ngCore.computed(
    (
      data = this._dataSg(),
      type = this._typeSg(),
      context = this._currentRendering.selectors.context.asSignal(),
      widgetId = this.widgetStore.renderingIdSg(),
      refId = this.widgetStore.refIdSg(),
    ) => {
      if (type === ents.core.display.chart.Type.Line) {
        // TODO: there should be a way to utilize `content` as a discriminate
        // union so that this casting is unnecessary
        const pointCollection =
          data as readonly ents.core.display.chart.data.Of<typeof type>[]
        const palette = this.paletteSg()

        /** Returning mapped point collections with assigned colors to each group value (dataset) */
        return pointCollection.map(
          collection =>
            ({
              config: {
                onItemClick: ({ onClick: actionRef, name }) => {
                  if (actionRef && context && widgetId) {
                    this._currentRendering.subfeatures.actions
                      .createActionHandler(`Chart.Point-${name}.Clicked`, {
                        ...context,
                        actionRef,
                        widget: {
                          id: widgetId,
                          refId,
                        },
                      })
                      .execute()
                  }
                },
              },
              data: {
                ...collection,
                values: collection.values?.map((value, i) => ({
                  ...value,
                  color: palette.getOrAssignColorFor(
                    value.name ?? i.toString(),
                  ),
                })),
              },
              type,
            }) satisfies ents.core.display.chart.Chart<ents.core.display.chart.Type.Line>,
        )
      }
      return undefined
    },
  )

  /** TODO */
  public chartsIfScatterSg = ngCore.computed(
    (
      data = this._dataSg(),
      type = this._typeSg(),
      context = this._currentRendering.selectors.context.asSignal(),
      widgetId = this.widgetStore.renderingIdSg(),
      refId = this.widgetStore.refIdSg(),
    ) => {
      if (type === ents.core.display.chart.Type.Scatter) {
        // TODO: there should be a way to utilize `content` as a discriminate
        // union so that this casting is unnecessary
        const pointCollection =
          data as readonly ents.core.display.chart.data.Of<typeof type>[]
        const palette = this.paletteSg()

        /** Returning mapped point collections with assigned colors to each group value (dataset) */
        return pointCollection.map(
          collection =>
            ({
              config: {
                onItemClick: ({ onClick: actionRef, name }) => {
                  if (actionRef && context && widgetId) {
                    this._currentRendering.subfeatures.actions
                      .createActionHandler(`Chart.Point-${name}.Clicked`, {
                        ...context,
                        actionRef,
                        widget: {
                          id: widgetId,
                          refId,
                        },
                      })
                      .execute()
                  }
                },
              },
              data: {
                ...collection,
                values: collection.values?.map((value, i) => ({
                  ...value,
                  color: palette.getOrAssignColorFor(
                    value.name ?? i.toString(),
                  ),
                })),
              },
              type,
            }) satisfies ents.core.display.chart.Chart<ents.core.display.chart.Type.Scatter>,
        )
      }
      return undefined
    },
  )

  /** TODO */
  public chartsIfBarSg = ngCore.computed(
    (
      data = this._dataSg(),
      type = this._typeSg(),
      context = this._currentRendering.selectors.context.asSignal(),
      widgetId = this.widgetStore.renderingIdSg(),
      refId = this.widgetStore.refIdSg(),
    ) => {
      if (type === ents.core.display.chart.Type.Bar) {
        // TODO: there should be a way to utilize `content` as a discriminate
        // union so that this casting is unnecessary
        const pointCollection =
          data as readonly ents.core.display.chart.data.Of<typeof type>[]
        const palette = this.paletteSg()

        /** Returning mapped point collections with assigned colors to each group value (dataset) */
        return pointCollection.map(
          collection =>
            ({
              config: {
                onItemClick: ({ onClick: actionRef, name }) => {
                  if (actionRef && context && widgetId) {
                    this._currentRendering.subfeatures.actions
                      .createActionHandler(`Chart.Point-${name}.Clicked`, {
                        ...context,
                        actionRef,
                        widget: {
                          id: widgetId,
                          refId,
                        },
                      })
                      .execute()
                  }
                },
              },
              data: {
                ...collection,
                values: collection.values?.map((value, i) => ({
                  ...value,
                  color: palette.getOrAssignColorFor(
                    value.name ?? i.toString(),
                  ),
                })),
              },
              type,
            }) satisfies ents.core.display.chart.Chart<ents.core.display.chart.Type.Bar>,
        )
      }
      return undefined
    },
  )

  /** TODO */
  public chartsIfStackedBarSg = ngCore.computed(
    (
      data = this._dataSg(),
      type = this._typeSg(),
      context = this._currentRendering.selectors.context.asSignal(),
      widgetId = this.widgetStore.renderingIdSg(),
      refId = this.widgetStore.refIdSg(),
    ) => {
      if (type === ents.core.display.chart.Type.StackedBar) {
        // TODO: there should be a way to utilize `content` as a discriminate
        // union so that this casting is unnecessary
        const pointCollection =
          data as readonly ents.core.display.chart.data.Of<typeof type>[]
        const palette = this.paletteSg()

        /** Returning mapped point collections with assigned colors to each group value (dataset) */
        return pointCollection.map(
          collection =>
            ({
              config: {
                onItemClick: ({ onClick: actionRef, name }) => {
                  if (actionRef && context && widgetId) {
                    this._currentRendering.subfeatures.actions
                      .createActionHandler(`Chart.Point-${name}.Clicked`, {
                        ...context,
                        actionRef,
                        widget: {
                          id: widgetId,
                          refId,
                        },
                      })
                      .execute()
                  }
                },
              },
              data: {
                ...collection,
                values: collection.values?.map((value, i) => ({
                  ...value,
                  color: palette.getOrAssignColorFor(
                    value.name ?? i.toString(),
                  ),
                })),
              },
              type,
            }) satisfies ents.core.display.chart.Chart<ents.core.display.chart.Type.StackedBar>,
        )
      }
      return undefined
    },
  )

  /** TODO */
  public chartsIfSunburstSg = ngCore.computed(
    (data = this._dataSg(), type = this._typeSg()) => {
      if (type === ents.core.display.chart.Type.Sunburst) {
        // TODO: there should be a way to utilize `content` as a discriminate
        // union so that this casting is unnecessary
        const trees = data as readonly ents.core.display.chart.data.Of<
          typeof type
        >[]

        /** Returning mapped point collections with assigned colors to each group value (dataset) */
        return trees.map<
          ents.core.display.chart.Chart<ents.core.display.chart.Type.Sunburst>
        >(tree => ({ data: tree, type }))
      }
      return undefined
    },
  )

  /** TODO */
  public chartsIfVennSg = ngCore.computed(
    (data = this._dataSg(), type = this._typeSg()) => {
      if (type === ents.core.display.chart.Type.Venn) {
        const vennData = data as readonly ents.core.display.chart.data.Of<
          typeof type
        >[]

        return vennData.map<
          ents.core.display.chart.Chart<ents.core.display.chart.Type.Venn>
        >(_venn => ({ data: _venn, type }))
      }
      return undefined
    },
  )
}
